/**
 * Demandes d'ouverture (spec 2026-10-25 § 5, lot L5, ARTO-188).
 *
 * Une demande est ce qu'un prospect envoie depuis artosera.com (le composeur,
 * la page options, le formulaire de contact). Elle vit en base plateforme, se
 * lit dans la console (menu Demandes) et se convertit en dossier d'ouverture
 * prérempli.
 *
 * Le schéma d'entrée est celui de la route publique `POST
 * /v1/public/demandes-ouverture`. Les identifiants de la sélection y sont des
 * chaînes quelconques : le serveur les confronte au registre des modules et
 * ignore (en les comptant) ceux qu'il ne connaît pas, plutôt que de refuser la
 * demande d'un prospect dont la page serait plus ancienne que l'API.
 */
import { z } from 'zod';
export const ETATS_DEMANDE_OUVERTURE = [
    'nouvelle',
    'qualifiee',
    'offre_envoyee',
    'convertie',
    'perdue',
];
/** États où la demande se travaille encore : qualifier, perdre, convertir. */
export const ETATS_DEMANDE_OUVERTS = [
    'nouvelle',
    'qualifiee',
    'offre_envoyee',
];
export const SOURCES_DEMANDE_OUVERTURE = ['composeur', 'contact', 'console'];
/** Les réponses du composeur pour un module ou un add-on. */
export const CHOIX_MODULE_DEMANDE = [
    'indispensable',
    'interessant',
    'plus-tard',
    'pas-pour-nous',
];
/** Les choix qui deviennent la présélection du dossier (§ 5.4). */
export const CHOIX_RETENUS = ['indispensable', 'interessant'];
/** Les réponses du composeur pour une prestation d'accompagnement. */
export const CHOIX_ACCOMPAGNEMENT = ['oui', 'a-discuter', 'non'];
/**
 * Les prestations d'accompagnement du composeur. Le site de galerie et la
 * boutique n'y sont pas : ce sont des services (`MODULES_SERVICES`), portés
 * par `selection.services`.
 */
export const PRESTATIONS_ACCOMPAGNEMENT = [
    'mise-en-place',
    'reprise-donnees',
    'reprise-complexe',
    'formation',
    'assistance',
    'evolutions-sur-mesure',
];
export function estPrestationAccompagnement(valeur) {
    return (typeof valeur === 'string' && PRESTATIONS_ACCOMPAGNEMENT.includes(valeur));
}
/** Nom du champ piège : invisible à l'écran, laissé vide par une personne. */
export const CHAMP_PIEGE_DEMANDE_OUVERTURE = 'telecopie';
/** En dessous, la saisie est trop rapide pour une personne (§ 5.2). */
export const DELAI_MIN_SAISIE_DEMANDE_MS = 3_000;
export const MESSAGE_DEMANDE_MAX = 5_000;
/** Un identifiant du registre tel qu'une page peut l'envoyer, connu ou non. */
const identifiantSchema = z
    .string()
    .trim()
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Identifiant invalide.');
const texteCourt = (max) => z.string().trim().max(max);
export const selectionDemandeEntreeSchema = z.strictObject({
    palier: identifiantSchema.optional(),
    modules: z
        .array(z.strictObject({ module: identifiantSchema, choix: z.enum(CHOIX_MODULE_DEMANDE) }))
        .max(64)
        .default([]),
    addons: z
        .array(z.strictObject({ addon: identifiantSchema, choix: z.enum(CHOIX_MODULE_DEMANDE) }))
        .max(64)
        .default([]),
    services: z.array(identifiantSchema).max(8).default([]),
    accompagnement: z
        .array(z.strictObject({ prestation: identifiantSchema, choix: z.enum(CHOIX_ACCOMPAGNEMENT) }))
        .max(16)
        .default([]),
});
/** Le corps de `POST /v1/public/demandes-ouverture`. */
export const demandeOuvertureEntreeSchema = z.strictObject({
    source: z.enum(['composeur', 'contact']),
    langue: z.enum(['fr', 'en']),
    contact: z.strictObject({
        nom: texteCourt(160).min(1, 'Votre nom est obligatoire.'),
        email: z.string().trim().toLowerCase().max(200).pipe(z.email('Adresse e-mail invalide.')),
        telephone: texteCourt(40).optional(),
        fonction: texteCourt(120).optional(),
    }),
    galerie: z.strictObject({
        nom: texteCourt(160).default(''),
        ville: texteCourt(120).optional(),
        /** Code ISO à deux lettres. */
        pays: z
            .string()
            .trim()
            .toUpperCase()
            .regex(/^[A-Z]{2}$/)
            .optional(),
        siteWeb: texteCourt(300).optional(),
    }),
    selection: selectionDemandeEntreeSchema.optional(),
    message: texteCourt(MESSAGE_DEMANDE_MAX).default(''),
    /** La version de la notice d'information affichée avec le formulaire. */
    consentement: z.strictObject({
        version: z
            .string()
            .trim()
            .regex(/^[a-z0-9][a-z0-9.-]{0,39}$/i),
    }),
    /** Temps écoulé entre l'ouverture du formulaire et l'envoi, mesuré par la page. */
    delaiSaisieMs: z
        .int()
        .min(0)
        .max(86_400_000 * 30),
    [CHAMP_PIEGE_DEMANDE_OUVERTURE]: z.string().max(200).optional(),
});
// ── Console ─────────────────────────────────────────────────────────────────
export const MOTIF_PERTE_MAX = 1_000;
//# sourceMappingURL=demandes-ouverture.js.map